import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Globe, DollarSign, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '@/i18n/config';
import { type SupportedCurrency } from '@/lib/money';
import { useAvailableCurrencies } from '@/hooks/useCurrencyAvailability';
import { useCurrency } from '@/contexts/CurrencyContext';
import { supabase } from '@/integrations/supabase/client';
import FlagEmoji from '@/components/ui/FlagEmoji';

interface CompactLocaleSelectorProps {
  variant?: 'navbar' | 'mobile' | 'mobile-dropdown';
  className?: string;
  onSelect?: () => void;
}

const LANGUAGE_META: Record<string, { native: string; flag: string; iso: string }> = {
  en: { native: 'English', flag: '🇬🇧', iso: 'gb' },
  se: { native: 'Svenska', flag: '🇸🇪', iso: 'se' },
  fr: { native: 'Français', flag: '🇫🇷', iso: 'fr' },
  es: { native: 'Español', flag: '🇪🇸', iso: 'es' },
  it: { native: 'Italiano', flag: '🇮🇹', iso: 'it' },
  ar: { native: 'العربية', flag: '🇲🇦', iso: 'ma' },
};

const ALL_LANGS = SUPPORTED_LANGUAGES as unknown as string[];

export function CompactLocaleSelector({ variant = 'navbar', className, onSelect }: CompactLocaleSelectorProps) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { displayCurrency, setDisplayCurrency } = useCurrency();
  const { options: currencies } = useAvailableCurrencies('customer_viewing');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentCurrency = currencies.find((c) => c.code === displayCurrency) || currencies[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLanguageChange = async (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    
    // Persist to profile (fire-and-forget)
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        supabase.from('profiles').update({ preferred_language: lang } as any).eq('id', data.user.id).then(() => {});
      }
    });
    
    const currentPath = window.location.pathname;
    const segments = currentPath.split('/').filter(Boolean);
    
    const firstSegment = segments[0];
    const isLangPrefixed = (SUPPORTED_LANGUAGES as readonly string[]).includes(firstSegment);
    
    let pathWithoutLang = currentPath;
    if (isLangPrefixed) {
      pathWithoutLang = '/' + segments.slice(1).join('/');
    }
    
    const newPath = lang === 'en' ? pathWithoutLang : `/${lang}${pathWithoutLang}`;
    navigate(newPath);
    setOpen(false);
    onSelect?.();
  };

  const handleCurrencyChange = (currency: SupportedCurrency) => {
    setDisplayCurrency(currency);
    setOpen(false);
    onSelect?.();
  };

  const currentLang = i18n.language === 'en' ? 'en' : i18n.language;
  const isRtl = i18n.dir?.() === 'rtl' || currentLang === 'ar';
  const currentMeta = LANGUAGE_META[currentLang] || LANGUAGE_META.en;

  const renderLangButton = (lang: string, isActive: boolean, onClick: () => void) => {
    const meta = LANGUAGE_META[lang] || { native: lang, flag: '', iso: lang };
    return (
      <button
        key={lang}
        onClick={onClick}
        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium transition-colors ${
          isActive
            ? 'bg-[var(--color-navy)] text-[var(--color-orange)]'
            : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
        }`}
      >
        <FlagEmoji iso={meta.iso} size={16} className="w-5 text-center" />
        <span className="flex-1 text-start">{meta.native}</span>
        {isActive && <Check className="h-3.5 w-3.5 text-[var(--color-orange)] shrink-0" />}
      </button>
    );
  };

  const renderCurrencySection = () => (
    <>
      <div className="px-3 py-2 border-t border-white/5 mt-1">
        <p className="text-[10px] font-semibold text-white/30 uppercase tracking-wider flex items-center gap-1">
          <DollarSign className="h-3 w-3" />
          {t('localeSelector.currency')}
        </p>
      </div>
      {currencies.map((currency) => (
        <button
          key={currency.code}
          onClick={() => handleCurrencyChange(currency.code as SupportedCurrency)}
          className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium transition-colors ${
            currency.code === displayCurrency
              ? 'bg-[var(--color-navy)] text-[var(--color-orange)]'
              : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
          }`}
        >
          <span className="w-5 text-center font-bold">{currency.symbol}</span>
          <span>{currency.code}</span>
          <span className="ms-auto text-[10px] text-white/25">{currency.name}</span>
        </button>
      ))}
      <div className="px-3 py-1.5 border-t border-white/5">
        <p className="text-[9px] text-white/20">{t('chrome.ratesViaFx')}</p>
      </div>
    </>
  );

  const renderLangSection = () => (
    <>
      <div className="px-3 py-2 border-b border-white/5">
        <p className="text-[10px] font-semibold text-white/30 uppercase tracking-wider flex items-center gap-1">
          <Globe className="h-3 w-3" />
          {t('localeSelector.language')}
        </p>
      </div>
      {ALL_LANGS.map((lang) => renderLangButton(lang, currentLang === lang, () => handleLanguageChange(lang)))}
    </>
  );

  if (variant === 'mobile-dropdown') {
    return (
      <div className={className || ''}>
        {renderLangSection()}
        {renderCurrencySection()}
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className={`px-4 py-3 ${className || ''}`}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">{t('localeSelector.languageAndCurrency')}</span>
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-white/[0.08] border border-white/10 hover:bg-white/[0.12] transition-colors min-w-0"
          >
            <FlagEmoji iso={currentMeta.iso} size={16} />
            <span className="text-xs font-bold min-w-0">{currentMeta.native}</span>
            <span className="text-[var(--color-orange)] font-bold flex-shrink-0">{currentCurrency.symbol}</span>
            <ChevronDown className={`h-3.5 w-3.5 text-white/50 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {open && (
          <div className="mt-2 bg-white/[0.04] border border-white/[0.08] overflow-hidden">
            {renderLangSection()}
            {renderCurrencySection()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className || ''}`} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center gap-2 h-10 px-3 text-sm font-semibold text-white bg-white/[0.06] border border-white/20 hover:bg-white/10 transition-colors min-w-0"
        title={`${currentMeta.native} · ${currentCurrency.code}`}
      >
        <FlagEmoji iso={currentMeta.iso} size={16} />
        <span className="text-xs font-bold min-w-0">{currentMeta.native}</span>
        <span className="text-[var(--color-orange)] font-bold flex-shrink-0">{currentCurrency.symbol}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-white/50 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      
      {open && (
        <div className={`absolute top-full mt-1.5 w-56 bg-[var(--color-navy)] border border-white/10 shadow-2xl z-50 overflow-hidden ${isRtl ? 'left-0' : 'right-0'}`}>
          {renderLangSection()}
          {renderCurrencySection()}
        </div>
      )}
    </div>
  );
}
